import type { AssetRef } from "@loumy/contracts";
import { useEffect, useMemo, useRef } from "react";
import type { JobSignals } from "../results/ResultMetaContext";
import { useJobDetail } from "../state/queries";
import { useUiStore } from "../state/uiStore";

// §11/D65 — „Job öffnen": lädt das Detail des im uiStore geöffneten Jobs und rehydriert,
// SOBALD das Detail zum aktuell angezeigten Tool (`coreId`) passt:
//   • settings → Draft (überschreibt Defaults/aktuellen Draft; SSOT bleibt der Submit-Parse, D12)
//   • inputs  → Media-Dropzones (previewUrl = presigned downloadUrl, D56)
// Die Settings/Inputs-Rehydrierung läuft **genau einmal je geöffnetem Job** (Ref-Guard): ein
// späterer Detail-Refetch (Live-Push, D47 — z.B. „Job fertig") darf laufende User-Edits NICHT
// überschreiben. Der **Output** + die **Signale** werden hingegen LIVE aus dem Detail gelesen,
// sodass Ergebnis/Player erscheinen, sobald der Job abgeschlossen ist. Passt das Detail nicht
// zum Tool (anderes Tab), bleibt alles leer. `coreId` darf undefined sein (Hook läuft
// unkonditional, Rules of Hooks).
//
// D68: zusätzlich (a) die persistierten `executed_pipeline_spec.signals` ausliefern (speisen den
// SpriteSheetPlayer via pack.meta + die Loop-Score-Anzeige) und (b) den vom Server veredelten
// Prompt (`signals.enhance.prompt`, D60) — sobald vorhanden (Job gelaufen) — sichtbar in den
// Draft-Prompt übernehmen (eigener Ref-Guard, EINMAL je Job, zerstört laufende Edits nicht).
export function useOpenedJob(coreId: string | undefined): {
  outputs: AssetRef[];
  resultUrls: Record<string, string>;
  signals: JobSignals;
  // D76 — Job-Status des geöffneten Jobs (JobDetail.job.status, z.B. "failed"), wenn das Detail
  // zum aktuellen Tool passt; sonst null. Speist den „Trotzdem generieren"-Pfad (spritesheet):
  // bei status "failed" + normalize.prepared===false bietet der Renderer das editierte Bild an.
  jobStatus: string | null;
} {
  const openedJobId = useUiStore((s) => s.openedJobId);
  const setDraft = useUiStore((s) => s.setDraft);
  const clearInputs = useUiStore((s) => s.clearInputs);
  const addInput = useUiStore((s) => s.addInput);

  const detail = useJobDetail(openedJobId);
  const data = detail.data;
  const matches = data != null && openedJobId != null && coreId != null && data.job.tool_id === coreId;

  // executed_pipeline_spec.signals ist über `.passthrough()` (pipeline.ts) untypisiert → schmaler Cast.
  const signals = useMemo<JobSignals>(() => {
    if (!matches || data == null) return {};
    const sig = (data.job.executed_pipeline_spec as { signals?: JobSignals } | null)?.signals;
    return sig && typeof sig === "object" ? sig : {};
  }, [matches, data]);

  const rehydratedFor = useRef<string | null>(null);
  useEffect(() => {
    if (!matches || data == null || openedJobId == null || coreId == null) return;
    if (rehydratedFor.current === openedJobId) return; // schon rehydriert → keine Edit-Überschreibung
    // D123/Codex-P1: Live-Check gegen den STORE — schließt jemand den Job (closeJob, z.B. die
    // Such-Palette beim frischen Compose), während das Detail noch lädt, darf der dann
    // eintreffende Effekt den neuen Draft nicht mehr mit den Job-Settings überschreiben.
    if (useUiStore.getState().openedJobId !== openedJobId) return;
    rehydratedFor.current = openedJobId;

    setDraft(coreId, (data.job.settings ?? {}) as Record<string, unknown>);
    clearInputs(coreId);
    for (const inp of data.inputs) {
      addInput(coreId, inp.role, { asset: inp.asset, previewUrl: inp.downloadUrl ?? undefined });
    }
  }, [matches, data, openedJobId, coreId, setDraft, clearInputs, addInput]);

  // D60/D68 — enhanced Prompt sichtbar in den Settings ersetzen, sobald der Server-Enhance
  // gelaufen ist (`enhanced=true`). Eigener Guard (nicht an die settings-Rehydrierung gekoppelt):
  // der enhanced Prompt liegt erst NACH dem Lauf vor, evtl. bei einem späteren Refetch — er soll
  // dann genau EINMAL einfließen, ohne den ganzen Draft neu zu setzen (nur das prompt-Feld).
  // Schutz vor Edit-Verlust (Codex-P1): nur ersetzen, wenn der aktuelle Draft-Prompt noch dem
  // ROH-Prompt des Jobs entspricht — hat der User inzwischen editiert, bleibt seine Eingabe.
  const enhancedSyncedFor = useRef<string | null>(null);
  useEffect(() => {
    if (!matches || data == null || openedJobId == null || coreId == null) return;
    if (enhancedSyncedFor.current === openedJobId) return;
    if (useUiStore.getState().openedJobId !== openedJobId) return; // s. o. (D123/Codex-P1)
    const enh = signals.enhance?.prompt as { text?: unknown; enhanced?: unknown } | undefined;
    if (enh?.enhanced !== true || typeof enh.text !== "string" || enh.text.trim().length === 0) return;
    const cur = useUiStore.getState().getDraft(coreId) ?? {};
    const rawPrompt = (data.job.settings as { prompt?: unknown } | null)?.prompt;
    if (cur.prompt !== undefined && cur.prompt !== rawPrompt) {
      enhancedSyncedFor.current = openedJobId; // User hat editiert → nicht überschreiben, aber als erledigt markieren
      return;
    }
    enhancedSyncedFor.current = openedJobId;
    setDraft(coreId, { ...cur, prompt: enh.text });
  }, [matches, data, openedJobId, coreId, signals, setDraft]);

  // Schließt der User den Job (closeJob → openedJobId=null; via „Neuer Task" in Studio.tsx oder
  // freshCompose in der Such-Palette), die Rehydrierungs-Guards zurücksetzen. Sonst rehydriert ein
  // erneutes Öffnen DESSELBEN Jobs nicht mehr (`rehydratedFor.current === openedJobId` greift) und
  // der frische Draft/die leeren Inputs blieben stehen, obwohl Output/Signale wieder zum Job passen
  // (Codex-P1). Sicher, weil bei keinem offenen Job keine laufenden User-Edits zu schützen sind —
  // der Edit-Schutz greift nur, solange ein Job offen ist (openedJobId !== null).
  useEffect(() => {
    if (openedJobId == null) {
      rehydratedFor.current = null;
      enhancedSyncedFor.current = null;
    }
  }, [openedJobId]);

  return useMemo(() => {
    if (!matches || data == null) return { outputs: [], resultUrls: {}, signals: {}, jobStatus: null };
    const resultUrls: Record<string, string> = {};
    for (const o of data.outputs) if (o.downloadUrl) resultUrls[o.asset.id] = o.downloadUrl;
    // D76 — Status mitliefern (z.B. "failed" beim fehlgeschlagenen Validierungs-Lauf).
    return { outputs: data.outputs.map((o) => o.asset), resultUrls, signals, jobStatus: data.job.status };
  }, [matches, data, signals]);
}
